/**
 * @category Utilities
 * @namespace Utilities.Helpers
 * @description Utility functions used across the application.
 */
// * @module src/utils/helpers

import React from "react";
import {
  deepCopy,
  getModelById,
  getModelsByIds,
  hasParentOrChildWithID,
} from "../components/leftSide/Models/modelLogic";
import units from "./units";
import HtmlTooltip from "../components/commonComponents/HtmlTooltip";
// import { getUpdatedUniqueIds } from "../components/rightSide/parameters/parameterLogic";

/**
 * Creates a warning object with given text and level, then adds it to the global
 * warning context and updates the *new* warning count (not yet seen by user).
 * @memberof Utilities.Helpers
 * @function
 * @param {string} message - A string message that the users sees relating to the error.
 * @param {number} level - Error level, 0 - info, 1 - warning, 2 - error.
 * @param {Function} setWarnings - Function to set the global warning context.
 * @param {Function} setNewWarningCount - Function to set new warning count.
 */
export function generateWarningObject(
  message,
  level,
  setWarnings,
  setNewWarningCount,
  isReportable = false,
  requestId = "none"
) {
  const now = new Date();
  const formattedTime = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const formattedDate = now.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedDateTime = `${formattedTime} ${formattedDate}`;

  setNewWarningCount((old) => old + 1);

  setWarnings((old) => {
    const newWarning = {
      message: message,
      level: level,
      time: formattedDateTime,
      reportable: isReportable,
      requestId: requestId,
    };

    // Keep only the last 99 warnings and add the new one (total: 100)
    const updatedWarnings = [...old, newWarning];
    if (updatedWarnings.length > 100) {
      return updatedWarnings.slice(-100);
    }
    return updatedWarnings;
  });
}

const symbols = {
  // Greek letters (lowercase)
  alpha: "α",
  beta: "β",
  gamma: "γ",
  delta: "δ",
  epsilon: "ε",
  zeta: "ζ",
  eta: "η",
  theta: "θ",
  iota: "ι",
  kappa: "κ",
  lambda: "λ",
  mu: "μ",
  nu: "ν",
  xi: "ξ",
  omicron: "ο",
  pi: "π",
  rho: "ρ",
  sigma: "σ",
  tau: "τ",
  upsilon: "υ",
  phi: "φ",
  chi: "χ",
  psi: "ψ",
  omega: "ω",
  // Greek letters (uppercase)
  Alpha: "Α",
  Beta: "Β",
  Gamma: "Γ",
  Delta: "Δ",
  Epsilon: "Ε",
  Zeta: "Ζ",
  Eta: "Η",
  Theta: "Θ",
  Iota: "Ι",
  Kappa: "Κ",
  Lambda: "Λ",
  Mu: "Μ",
  Nu: "Ν",
  Xi: "Ξ",
  Omicron: "Ο",
  Pi: "Π",
  Rho: "Ρ",
  Sigma: "Σ",
  Tau: "Τ",
  Upsilon: "Υ",
  Phi: "Φ",
  Chi: "Χ",
  Psi: "Ψ",
  Omega: "Ω",
  // Symbols
  inf: "∞",
};

export function formatParamNameToString(input) {
  let replaced = input.replace(/greek\((.*?)\)/g, (match, p1) => {
    return symbols[p1] || match;
  });

  // Replace symbols
  replaced = replaced.replace(/symbol\((.*?)\)/g, (match, p1) => {
    return symbols[p1] || match;
  });

  return replaced;
}

/**
 * Converts greek letters and symbols in the input string to unicode.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} input - Finds greek letters and symbols in the input string and replaces
 * them with their corresponding unicode characters.
 * @returns {string} Returns the input string with greek letters and symbols converted to unicode.
 */
export function processSymbols(input) {
  // Replace Greek letters
  let replaced = input.replace(/greek\((.*?)\)/g, (match, p1) => {
    return symbols[p1] || match;
  });

  // Replace symbols
  replaced = replaced.replace(/symbol\((.*?)\)/g, (match, p1) => {
    return symbols[p1] || match;
  });

  // Handle subscripts and superscripts
  let jsx = [];
  let buffer = "";
  let i = 0;
  while (i < replaced.length) {
    if (replaced[i] === "_") {
      jsx.push(buffer);
      buffer = "";
      if (replaced[i + 1] === "(") {
        let endPos = replaced.indexOf(")", i + 2);
        if (endPos !== -1) {
          jsx.push(<sub key={i}>{replaced.slice(i + 2, endPos)}</sub>);
          i = endPos + 1;
        } else {
          // handle error
          jsx.push("_");
          i++;
        }
      } else {
        jsx.push(<sub key={i}>{replaced[i + 1]}</sub>);
        i += 2;
      }
    } else if (replaced[i] === "^") {
      jsx.push(buffer);
      buffer = "";
      if (replaced[i + 1] === "(") {
        let endPos = replaced.indexOf(")", i + 2);
        if (endPos !== -1) {
          jsx.push(<sup key={i}>{replaced.slice(i + 2, endPos)}</sup>);
          i = endPos + 1;
        } else {
          // handle error
          jsx.push("^");
          i++;
        }
      } else {
        jsx.push(<sup key={i}>{replaced[i + 1]}</sup>);
        i += 2;
      }
    } else {
      buffer += replaced[i];
      i++;
    }
  }
  jsx.push(buffer);

  return jsx;
}

/**
 * This function takes in a string and returns a JSX element that contains the input string with greek
 * letters transformed to unicode.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} param0 - takes in a property called 'input' which is a string that can contain
 * greek letters and symbols with our own encoding.
 * @returns {JSX.Element} Returns a JSX element that contains the input string with greek letters
 * transformed to unicode.
 */
export function FormattedText({ input }) {
  return <>{processSymbols(input)}</>;
}

/**
 * This function takes in a numeric value and returns a numeric value of how many pixels wide is the
 * input as a string with that many characters.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - A numeric value that represents the size of input in characters.
 * @returns {number} Returns a numeric value of how many pixels wide is the input as a string.
 */
export function useInputSize(value) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = "1.1rem system-ui, sans-serif"; // Match the font size and family of the input
  const inputSize = ctx.measureText(value || " ").width;

  return inputSize;
}

/**
 * This function checks if the input string is a valid number.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} value - A string that is checked if it is a valid number.
 * @returns {boolean} Returns true if the input string is a valid number, false otherwise.
 */
export function isAllowedNum(value) {
  if (/^[-+]?\d*\.?\d*([Ee][+-]?\d*)?$/.test(value)) {
    return true;
  } else {
    return false;
  }
}

/**
 * This function is used extensively in the application to check if 2 objects are equal by their values.
 * This function goes through all the properties, all the way down the object tree, and checks if the objects
 * indeed have the same values.
 * @function
 * @memberof Utilities.Helpers
 * @param {Object} obj1 - First object to compare.
 * @param {Object} obj2 - Second object to compare.
 * @returns {boolean} Returns true if the objects are equal, false otherwise.
 */
export function isDeepEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true;
  }

  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }

    const value1 = obj1[key];
    const value2 = obj2[key];

    if (!isDeepEqual(value1, value2)) {
      return false;
    }
  }

  return true;
}

/**
 * This function returns one of 20 colors based on the input number.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} number - A number that is used to determine which color to return.
 * @returns {string} Returns a string that represents a color in hex format.
 * @example
 * // returns "#1f77b4"
 * getColor(0);
 *
 * @example
 * // returns "#ff7f0e"
 * getColor(1);
 *
 * @example
 * // returns "#ff7f0e"
 * getColor(21);
 */
export function getColor(number) {
  const colors = [
    "#1f77b4", // blue
    "#ff7f0e", // orange
    "#2ca02c", // green
    "#d62728", // red
    "#9467bd", // purple
    "#8c564b", // brown
    "#e377c2", // pink
    "#7a0419", // dark red
    "#bcbd22", // olive
    "#17becf", // cyan
    "#aec7e8", // light blue
    "#ffbb78", // light orange
    "#98df8a", // light green
    "#ff9896", // light red
    "#c5b0d5", // light purple
    "#c49c94", // light brown
    "#f7b6d2", // light pink
    "#c7c7c7", // light gray
    "#dbdb8d", // light olive
    "#9edae5", // light cyan
  ];

  return colors[number % colors.length];
}

/**
 * This function takes graph data object and returns representatives of each curve in the graph (one representative per
 * file or model-quantity pair). This is used to display the legend.
 * @function
 * @memberof Utilities.Helpers
 * @param {Object} data - This is the graph data object that contains all the data for the graph.
 * @returns {Array.<Object>} Returns an array of objects that represent each curve in the graph.
 */
export function getRepresentatives(data) {
  let representatives = [];
  let addedIds = { fileId: [], modelId: [] };

  for (let i = 0; i < data.length; i++) {
    let item = data[i];
    let graphId = hasProperty(item, "graphId") ? item.graphId : -1;

    if (item.fileId !== undefined) {
      // This means we are dealing with file representative
      // Check if we've already added an item with this id and legendgroup
      if (
        !addedIds["fileId"].some(
          (id) =>
            id.id === item["fileId"] &&
            id.legendgroup === item.legendgroup &&
            id.graphId === graphId
        )
      ) {
        // If not, add it to the representatives array and record the id and legendgroup
        representatives.push({
          name: item.name,
          fileId: item["fileId"],
          graphId: graphId,
        });
        addedIds["fileId"].push({
          id: item["fileId"],
          legendgroup: item.legendgroup,
          graphId: graphId,
        });
      }
    } else if (item.modelId !== undefined) {
      // this means we are dealing with model representative
      if (
        !addedIds["modelId"].some(
          (id) =>
            id.id === item["modelId"] &&
            id.legendgroup === item.legendgroup &&
            id.quantity === item.quantity &&
            id.graphId === graphId
        )
      ) {
        // If not, add it to the representatives array and record the id and legendgroup
        representatives.push({
          name: item.name,
          modelId: item["modelId"],
          quantity: item.quantity,
          graphId: graphId,
        });
        addedIds["modelId"].push({
          id: item.modelId,
          legendgroup: item.legendgroup,
          quantity: item.quantity,
          graphId: graphId,
        });
      }
    } else {
      // If not, add it to the representatives array and record the id and legendgroup
      representatives.push({
        name: item.name,
        variableId: item.variableId,
        graphId: graphId,
      });
    }

    // Decide whether to use fileId or modelId for this item
    // let idKey = item.fileId !== undefined ? "fileId" : "modelId";
  }

  return representatives;
}

/**
 * This function checks if uploaded files match the supported file extension criteria. If they do, it
 * calls the processFiles function that processes the files, otherwise it displays an alert.
 * @function
 * @memberof Utilities.Helpers
 * @param {React.ChangeEvent<HTMLInputElement>} event - This is event object of file being uploaded.
 * @param {Array.<string>} supportedExtensions - This is an array of supported file extensions.
 * @param {Function(Array.<File>)} processFiles - This is a function that processes the files if they fit the extension criteria.
 */
export const handleFileChange = (event, supportedExtensions, processFiles) => {
  const files = Array.from(event.target.files);
  // const supportedExtensions = ["json"];

  const filteredFiles = files.filter((file) => {
    const fileExtension = file.name.split(".").pop();
    return supportedExtensions.includes(fileExtension);
  });

  const unsupportedFiles = files.filter(
    (file) => !filteredFiles.includes(file)
  );

  if (unsupportedFiles.length > 0) {
    const unsupportedFileNames = unsupportedFiles
      .map((file) => file.name)
      .join(", ");
    alert(
      `The following files have unsupported formats and will not be processed: ${unsupportedFileNames}`
    );
  }

  if (filteredFiles.length > 0) {
    processFiles(filteredFiles)
      .then(() => {
        // If files were processed successfully, clear the file input
        event.target.value = "";
      })
      .catch(() => {
        // If there was an error processing the files, clear the file input
        event.target.value = "";
      });
  } else {
    console.warn("No were files loaded in!");
  }
};

/**
 * This function takes in file content and name as string and causes a download of the file.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} fileString - This is a string that represents the file content.
 * @param {string} fileName - This is as string that represents the file name.
 */
export function downloadStringTxtFile(fileString, fileName, format = ".txt") {
  const blob = new Blob([fileString], {
    type: "text/plain",
  });
  const link = document.createElement("a");

  link.href = URL.createObjectURL(blob);
  link.download = fileName + format;

  link.dispatchEvent(
    new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );

  // Remove the link from the DOM
  setTimeout(() => {
    window.URL.revokeObjectURL(link.href);
    link.remove();
  }, 100);
}

/**
 * This takes in an array of data points and returns an array of data points that are within the
 * range of min and max.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} data - This is a list of data points.
 * @param {number} min - This is a number that represents the minimum value of the x axis.
 * @param {number} max - This is a number that represents the maximum value of the x axis.
 * @returns {Array.<Object>} Returns an array of data points that are within the range of min and max.
 */
export function filterCurveDataToRange(data, min = null, max = null) {
  if (min !== null && max !== null) {
    return data.filter((entry) => entry.x >= min && entry.x <= max);
  } else if (min === null && max !== null) {
    return data.filter((entry) => entry.x <= max);
  } else if (min !== null && max === null) {
    return data.filter((entry) => entry.x >= min);
  } else {
    return data;
  }
}

/**
 * This function checks if the input value is a number.
 * @function
 * @memberof Utilities.Helpers
 * @param {*} value - This is a value that is checked if it is a number.
 * @returns {boolean} Returns true if the input value is a number, false otherwise.
 */
export function isNumber(value) {
  return typeof value === "number" && !isNaN(value);
}

/**
 * This is a function that rounds a number to a given precision
 * @function
 * @memberof Utilities.Helpers
 * @param {number} number - This is a number that is rounded to the given precision.
 * @param {number} precision - This is a precision to which the number is rounded.
 * @returns {number} Returns a number that is rounded to the given precision.
 */
export function toPrecision(number, precision) {
  if (isNumber(number)) {
    return parseFloat(number.toFixed(precision));
  } else {
    return number;
  }
}

/**
 * This is function that checks if the object contains given property. We use this function, because it is
 * needed a lot and it simplifies the syntax.
 * @function
 * @memberof Utilities.Helpers
 * @param {Object} object - This is an object that we want to check for a property.
 * @param {string} property - This is a property that we want to check if it exists in the object.
 * @returns {boolean} Returns true if the object has the property, false otherwise.
 */
export function hasProperty(object, property) {
  return (
    object &&
    typeof object === "object" &&
    Object.prototype.hasOwnProperty.call(object, property) &&
    object[property] !== undefined
  );
}
/**
 * This function takes in a list of graphs and returns a dictionary of all the curve ranges used in graphs.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} graphs - This is a list of all displayed graphs.
 * @returns {Object} Returns a dictionary of all the curve ranges used in graphs.
 * 
 * @example
 * // returns 
{
  "1": { // This represents model id
    "5": [ // This represents quantity
      -108.88,
      319.29
    ]
  },
  "2": {
    "10": [
      -108.88,
      319.29
    ]
  }
}
* // Number as a key represents model id, and the array represents the ranges of the curve
* // through all the graphs.
*/
export function getRangesForModelsFromGraphs(graphs) {
  let modelRangeDictionary = {};

  for (let i = 0; i < graphs.length; i++) {
    const modelsInGraph = graphs[i].containedModels;
    const unitX = graphs[i].layout.xaxis.unit || units[0];
    const inverseUnit = isInverseUnit(unitX);

    let outPutUnitsInGraph = [];
    for (let j = 0; j < modelsInGraph.length; j++) {
      const modelIdAndQuantity = modelsInGraph[j];
      let outputUnit = units[0];
      if (hasProperty(modelIdAndQuantity, "originalXUnit")) {
        outputUnit = modelIdAndQuantity.originalXUnit;
      }
      if (!outPutUnitsInGraph.includes(outputUnit)) {
        outPutUnitsInGraph.push(outputUnit);
      }
    }
    if (outPutUnitsInGraph.length === 0) {
      continue;
    }

    let rangeUnitx = [];
    for (let j = 0; j < outPutUnitsInGraph.length; j++) {
      const ranges = convertRangeRequestFromUnitsToCm1(
        unitX,
        graphs[i].layout.xaxis.range,
        1000,
        outPutUnitsInGraph[j]
      );

      const isInverseOriginalXUnit = isInverseUnit(outPutUnitsInGraph[j]);
      let xRange = {
        min: ranges[0].min,
        max: ranges[0].max,
        nPoints: ranges[0].nPoints,
        isInverseUnit: !(inverseUnit === isInverseOriginalXUnit),
        isLogarithmic: graphs[i].layout.xaxis.type === "log",
        graphId: graphs[i].id,

      };
      if (ranges.length === 2) {
        xRange = {
          ...xRange,
          min: ranges[1].min,
          max: ranges[1].max,
          nPoints: ranges[1].nPoints,
          minNeg: ranges[0].min,
          maxNeg: ranges[0].max,
          nPointsNeg: ranges[0].nPoints,
        };
      }
      rangeUnitx.push({ xRange: xRange, unit: outPutUnitsInGraph[j] });
    }

    for (let j = 0; j < modelsInGraph.length; j++) {
      const modelIdAndQuantity = modelsInGraph[j];
      let outputUnit = units[0];
      if (hasProperty(modelIdAndQuantity, "originalXUnit")) {
        outputUnit = modelIdAndQuantity.originalXUnit;
      }
      let xRange = rangeUnitx.find(
        (entry) => entry.unit === outputUnit
      )?.xRange;

      if (hasProperty(modelRangeDictionary, modelIdAndQuantity.modelId)) {
        if (
          hasProperty(
            modelRangeDictionary[modelIdAndQuantity.modelId],
            modelIdAndQuantity.quantity
          )
        ) {
          modelRangeDictionary = {
            ...modelRangeDictionary,
            [modelIdAndQuantity.modelId]: {
              ...modelRangeDictionary[modelIdAndQuantity.modelId],
              [modelIdAndQuantity.quantity]: [
                ...modelRangeDictionary[modelIdAndQuantity.modelId][
                  modelIdAndQuantity.quantity
                ],
                xRange,
              ],
            },
          };
        } else {
          modelRangeDictionary = {
            ...modelRangeDictionary,
            [modelIdAndQuantity.modelId]: {
              ...modelRangeDictionary[modelIdAndQuantity.modelId],
              [modelIdAndQuantity.quantity]: [xRange],
            },
          };
        }
      } else {
        modelRangeDictionary = {
          ...modelRangeDictionary,
          [modelIdAndQuantity.modelId]: {
            [modelIdAndQuantity.quantity]: [xRange],
          },
        };
      }
    }
  }

  return modelRangeDictionary;
}

/**
 * This function takes in a list of curves returned from backend and returns a dictionary of all the curves.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} returnedCurves - These are the curves returned from the backend.
 * @returns {Object} Returns a dictionary of all the returned curves.
 */
export function getReturnedCurveDictionaryByGraphId(returnedCurves) {
  let dictionary = {};

  for (let i = 0; i < returnedCurves.length; i++) {
    const curve = returnedCurves[i];
    const modelId = curve.modelid;
    const graphId = curve.graphId;
    const quantity = curve.curves[0].quantity; // Store this to ensure consistency
    const coordinates = curve.curves[0].coordinates;

    if (hasProperty(dictionary, graphId)) {
      dictionary[graphId] = [
        ...dictionary[graphId],
        {
          modelId: modelId,
          coordinates: coordinates,
          quantity: quantity,
        },
      ];
    } else {
      dictionary[graphId] = [
        {
          modelId: modelId,
          coordinates: coordinates,
          quantity: quantity,
        },
      ];
    }
  }

  return dictionary;
}
/**
 * This function translates a number into scientific notation if the number is longer than 5 digits.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} num - This is number that we want to translate into scientific notation.
 * @param {number} decPlaces - This is a number that represents the number of decimal places we want to use.
 * Default is 1.
 * @returns {number} Returns a number in scientific notation if the number is longer than 5 digits.
 */
export function toScientificNotation(num, decPlaces = 1) {
  if (typeof num != "number") {
    return "N/A";
  }
  if (Math.abs(num) > 10 ** decPlaces || Math.abs(num) < 1) {
    return num.toExponential(decPlaces);
  } else {
    return num.toPrecision(decPlaces + 1);
  }
}

/**
 * This is a function that takes in a list of data points and edges and returns a list of data points split
 * into segments based on edges.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} dataPoints - List of data points.
 * @param {Array.<Array.<number>>} edges - List of edges.
 * @returns {Array.<Object>} Returns a list of data points split into segments based on edges.
 */
export function splitCurveIntoSegmentsByEdges(dataPoints, edges) {
  const edgesWithUnits = edges.map((edge) => {
    if (hasProperty(edge, "unit") ){
      return edge; // Edge already has unit property, return as-is
    } else {
      return {...edge, unit: units[0]}; // Add default unit (cm^-1)
    }
  });
  // Sort dataPoints by x-value (create shallow copy to avoid mutating original)
  const sortedDataPoints = [...dataPoints].sort((a, b) => a.x - b.x);

  // Split dataWithin into segments based on edges
  let segments = [];
  let currentSegment = [];
  sortedDataPoints.forEach((point) => {
    if (
      edgesWithUnits.some(
        (edge) =>
          edge.min !== "" &&
          edge.max !== "" &&
          cm1ToAnyUnit(edge.unit, point.x) > edge.min &&
          cm1ToAnyUnit(edge.unit, point.x) < edge.max
      )
    ) {
      if (currentSegment.length > 0) {
        segments.push(currentSegment);
        currentSegment = [];
      }
    } else {
      currentSegment.push(point);
    }
  });
  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
}

/**
 * This function combines segments of a curve into a single curve.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Array.<Object>>} segments - Segments of a curve.
 * @returns {Array.<Object>} Returns a single curve.
 */
export function segmentsIntoSingleCurve(segments) {
  let singleCurve = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    singleCurve = [...singleCurve, ...segment];
  }

  return singleCurve;
}

/**
 * This is a function that updates model data based on the changes in value groups.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} modelsToUpdate - This is a list of models that we want to update.
 * @param {Array.<Object>} valGroups - This is a list of value groups that we want to use to update the models.
 * @returns {Array.<Object>} Returns a list of models with updated values.
 */
export function updateModelsOnGroupChanges(modelsToUpdate, valGroups) {
  const modelDataCopy = deepCopy(modelsToUpdate);
  const updatedIds = [];

  for (let i = 0; i < modelDataCopy.length; i++) {
    const model = modelDataCopy[i];
    handleDeepGroups(model);
  }
  return {
    udpatedModels: modelDataCopy,
    updatedIds: updatedIds,
  };

  function handleDeepGroups(model) {
    let includeModelID = false;

    for (let i = 0; i < model.modelParams.length; i++) {
      const param = model.modelParams[i];
      if (hasProperty(param, "group")) {
        includeModelID = true;
        const group = valGroups.find(
          (entry) => entry.groupNumber == param.group
        );
        model.modelParams[i] = {
          ...model.modelParams[i],
          value: group.value,
          hardMax: group.hardMax != null ? group.hardMax : "",
          hardMin: group.hardMin != null ? group.hardMin : "",
        };
        if (group.fixed != null) {
          model.modelParams[i] = {
            ...model.modelParams[i],
            customFixed: group.fixed,
          };
        }
      }
    }

    if (hasProperty(model, "recParams")) {
      for (let i = 0; i < model.recParams.length; i++) {
        const paramArray = model.recParams[i];
        for (let j = 0; j < paramArray.length; j++) {
          const param = paramArray[j];
          if (hasProperty(param, "group")) {
            includeModelID = true;
            const group = valGroups.find(
              (entry) => entry.groupNumber == param.group
            );
            model.recParams[i][j] = {
              ...model.recParams[i][j],
              value: group.value,
              hardMax: group.hardMax != null ? group.hardMax : "",
              hardMin: group.hardMin != null ? group.hardMin : "",
            };
            if (group.fixed != null) {
              model.recParams[i][j] = {
                ...model.recParams[i][j],
                customFixed: group.fixed,
              };
            }
            model.recTableRows[i] = {
              ...model.recTableRows[i],
              [param.name]: group.value,
            };
          }
        }
      }
    }

    if (hasProperty(model, "lineshapeData")) {
      for (let i = 0; i < model.lineshapeData.length; i++) {
        if (hasProperty(model.lineshapeData[i], "lineParams")) {
          for (let j = 0; j < model.lineshapeData[i].lineParams.length; j++) {
            for (
              let k = 0;
              k < model.lineshapeData[i].lineParams[j].length;
              k++
            ) {
              const param = model.lineshapeData[i].lineParams[j][k];
              if (hasProperty(param, "group")) {
                includeModelID = true;
                const group = valGroups.find(
                  (entry) => entry.groupNumber == param.group
                );
                model.lineshapeData[i].lineParams[j][k] = {
                  ...model.lineshapeData[i].lineParams[j][k],
                  value: group.value,
                  hardMax: group.hardMax != null ? group.hardMax : "",
                  hardMin: group.hardMin != null ? group.hardMin : "",
                };
                if (group.fixed != null) {
                  model.lineshapeData[i].lineParams[j][k] = {
                    ...model.lineshapeData[i].lineParams[j][k],
                    customFixed: group.fixed,
                  };
                }
                if (hasProperty(model.lineshapeData[i], "lineTableRows")) {
                  model.lineshapeData[i].lineTableRows[j] = {
                    ...model.lineshapeData[i].lineTableRows[j],
                    [param.name]: group.value,
                  };
                }
              }
            }
          }
        }
      }
    }

    if (includeModelID) {
      updatedIds.push(model.FE_ID);
    }

    if (model.subModels.length > 0) {
      model.subModels.forEach((model) => {
        handleDeepGroups(model);
      });
    }
  }
}

/**
 * This is a function that updates model data based on the changes in value groups.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} modelsToUpdate - This is a list of models that we want to update.
 * @param {Array.<Object>} valGroups - This is a list of value groups that we want to use to update the models.
 * @returns {Array.<Object>} Returns a list of models with updated values.
 */
export function updateGroupsOnModelChanges(modelsToCheck, valGroups) {
  const modelDataCopy = deepCopy(modelsToCheck);
  const valGroupsCopy = deepCopy(valGroups);
  let modelsOk = true;
  let valGroupsOk = true;
  let valueCounts = {};
  let clashingGroups = {};
  let updatedIds = [];

  for (let i = 0; i < valGroupsCopy.length; i++) {
    if (!hasProperty(valGroupsCopy[i], "value")) {
      valGroupsCopy[i].value = null;
      valGroupsOk = false;
    }
    if (!hasProperty(valGroupsCopy[i], "hardMax")) {
      valGroupsCopy[i].hardMax = null;
      valGroupsOk = false;
    }
    if (!hasProperty(valGroupsCopy[i], "hardMin")) {
      valGroupsCopy[i].hardMin = null;
      valGroupsOk = false;
    }
    if (!hasProperty(valGroupsCopy[i], "fixed")) {
      valGroupsCopy[i].fixed = false;
      valGroupsOk = false;
    }
  }

  for (let i = 0; i < modelDataCopy.length; i++) {
    const model = modelDataCopy[i];
    handleDeepGroups(model);
  }

  for (let i = 0; i < valGroupsCopy.length; i++) {
    const group = valGroupsCopy[i];
    if (group.memberCount < 1 && hasProperty(valueCounts, group.groupNumber)) {
      valGroupsCopy[i].memberCount = valueCounts[group.groupNumber];
      valGroupsOk = false;
    }
    if (
      group.memberCount < 1 &&
      (group.value !== null ||
        group.hardMax !== null ||
        group.hardMin !== null ||
        (group.fixed !== false && group.fixed !== null))
    ) {
      valGroupsOk = false;
      valGroupsCopy[i].memberCount = 0;
      valGroupsCopy[i].value = null;
      valGroupsCopy[i].hardMax = null;
      valGroupsCopy[i].hardMin = null;
      valGroupsCopy[i].fixed = false;
    }
    if (group.memberCount < 0) {
      valGroupsOk = false;
      valGroupsCopy[i].memberCount = 0;
    }
    if (
      group.memberCount !== 0 &&
      !hasProperty(valueCounts, group.groupNumber)
    ) {
      valGroupsOk = false;
      valGroupsCopy[i].memberCount = 0;
      valGroupsCopy[i].value = null;
      valGroupsCopy[i].hardMax = null;
      valGroupsCopy[i].hardMin = null;
      valGroupsCopy[i].fixed = false;
    }
    if (
      group.memberCount !== 0 &&
      hasProperty(valueCounts, group.groupNumber) &&
      valueCounts[group.groupNumber] !== group.memberCount
    ) {
      valGroupsOk = false;
      valGroupsCopy[i].memberCount = valueCounts[group.groupNumber];
    }
    if (hasProperty(clashingGroups, group.groupNumber)) {
      valGroupsOk = false;
      if (clashingGroups[group.groupNumber].memberCount === group.memberCount) {
        valGroupsCopy[i].value =
          clashingGroups[group.groupNumber].incomingValue;
        valGroupsCopy[i].hardMax =
          clashingGroups[group.groupNumber].incomingHardMax;
        valGroupsCopy[i].hardMin =
          clashingGroups[group.groupNumber].incomingHardMin;
        valGroupsCopy[i].fixed =
          clashingGroups[group.groupNumber].incomingFixed;
        valGroupsOk = false;
        delete clashingGroups[group.groupNumber];
      } else {
        valGroupsCopy[i].memberCount -=
          clashingGroups[group.groupNumber].memberCount;
      }
    }
  }
  let response = {};
  if (!modelsOk) {
    response.updatedModels = deepCopy(modelDataCopy);
    response.updatedIds = deepCopy(updatedIds);
  }
  if (Object.keys(clashingGroups).length > 0) {
    response.clashingGroups = deepCopy(clashingGroups);
  }
  if (!valGroupsOk) {
    response.updatedValGroups = deepCopy(valGroupsCopy);
  }
  return response;

  function handleDeepGroups(model) {
    let includeModelID = false;
    for (let i = 0; i < model.modelParams.length; i++) {
      const param = model.modelParams[i];
      if (hasProperty(param, "group")) {
        const groupindex = valGroupsCopy.findIndex(
          (group) => group.groupNumber === param.group
        );
        if (groupindex !== -1) {
          if (hasProperty(valueCounts, param.group)) {
            valueCounts[param.group] += 1;
          } else {
            valueCounts[param.group] = 1;
          }
        }
        if (valGroupsCopy[groupindex].value === null) {
          valGroupsOk = false;
          valGroupsCopy[groupindex].value = param.value;
          valGroupsCopy[groupindex].hardMax =
            hasProperty(param, "hardMax") && param.hardMax !== ""
              ? param.hardMax
              : null;
          valGroupsCopy[groupindex].hardMin =
            hasProperty(param, "hardMin") && param.hardMin !== ""
              ? param.hardMin
              : null;
          valGroupsCopy[groupindex].fixed = hasProperty(param, "customFixed")
            ? param.customFixed
            : false;
        } else {
          const valueConflict = param.value !== valGroupsCopy[groupindex].value;
          const hardMaxConflict = !(
            ((valGroupsCopy[groupindex].hardMax === null ||
              valGroupsCopy[groupindex].hardMax === "") &&
              (!hasProperty(param, "hardMax") || param.hardMax === "")) ||
            (hasProperty(param, "hardMax") &&
              valGroupsCopy[groupindex].hardMax === param.hardMax)
          );
          const hardMinConflict = !(
            ((valGroupsCopy[groupindex].hardMin === null ||
              valGroupsCopy[groupindex].hardMin === "") &&
              (!hasProperty(param, "hardMin") || param.hardMin === "")) ||
            (hasProperty(param, "hardMin") &&
              valGroupsCopy[groupindex].hardMin === param.hardMin)
          );
          const fixedConflict = !(
            ((valGroupsCopy[groupindex].fixed === null ||
              valGroupsCopy[groupindex].fixed === "") &&
              (!param.customFixed || param.customFixed === "")) ||
            valGroupsCopy[groupindex].fixed === param.customFixed
          );
          if (
            valueConflict ||
            hardMaxConflict ||
            hardMinConflict ||
            fixedConflict
          ) {
            includeModelID = true;
            modelsOk = false;
            model.modelParams[i] = {
              ...model.modelParams[i],
              value: valGroupsCopy[groupindex].value,
              hardMax:
                valGroupsCopy[groupindex].hardMax != null
                  ? valGroupsCopy[groupindex].hardMax
                  : "",
              hardMin:
                valGroupsCopy[groupindex].hardMin != null
                  ? valGroupsCopy[groupindex].hardMin
                  : "",
            };
            if (valGroupsCopy[groupindex].fixed != null) {
              model.modelParams[i] = {
                ...model.modelParams[i],
                customFixed: valGroupsCopy[groupindex].fixed,
              };
            }
            if (hasProperty(clashingGroups, param.group)) {
              clashingGroups = {
                ...clashingGroups,
                [param.group]: {
                  ...clashingGroups[param.group],
                  memberCount: clashingGroups[param.group].memberCount + 1,
                },
              };
            } else {
              clashingGroups = {
                ...clashingGroups,
                [param.group]: {
                  currentValue: valGroupsCopy[groupindex].value,
                  incomingValue: param.value,
                  currentHardMax: valGroupsCopy[groupindex].hardMax,
                  incomingHardMax: param.hardMax,
                  currentHardMin: valGroupsCopy[groupindex].hardMin,
                  incomingHardMin: param.hardMin,
                  currentFixed: valGroupsCopy[groupindex].fixed,
                  incomingFixed: param.customFixed,
                  memberCount: 1,
                  // Track which properties are conflicting
                  conflicts: {
                    value: valueConflict,
                    hardMax: hardMaxConflict,
                    hardMin: hardMinConflict,
                    fixed: fixedConflict,
                  },
                },
              };
            }
          }
        }
      }
    }

    if (hasProperty(model, "recParams")) {
      for (let i = 0; i < model.recParams.length; i++) {
        const paramArray = model.recParams[i];
        for (let j = 0; j < paramArray.length; j++) {
          const param = paramArray[j];
          if (hasProperty(param, "group")) {
            const groupindex = valGroupsCopy.findIndex(
              (group) => group.groupNumber === param.group
            );
            if (groupindex !== -1) {
              if (hasProperty(valueCounts, param.group)) {
                valueCounts[param.group] += 1;
              } else {
                valueCounts[param.group] = 1;
              }
            }
            if (valGroupsCopy[groupindex].value === null) {
              valGroupsOk = false;
              valGroupsCopy[groupindex].value = param.value;
              valGroupsCopy[groupindex].hardMax =
                hasProperty(param, "hardMax") && param.hardMax !== ""
                  ? param.hardMax
                  : null;
              valGroupsCopy[groupindex].hardMin =
                hasProperty(param, "hardMin") && param.hardMin !== ""
                  ? param.hardMin
                  : null;
              valGroupsCopy[groupindex].fixed = hasProperty(
                param,
                "customFixed"
              )
                ? param.customFixed
                : false;
            } else {
              const valueConflict =
                param.value !== valGroupsCopy[groupindex].value;
              const hardMaxConflict = !(
                ((valGroupsCopy[groupindex].hardMax === null ||
                  valGroupsCopy[groupindex].hardMax === "") &&
                  (!hasProperty(param, "hardMax") || param.hardMax === "")) ||
                (hasProperty(param, "hardMax") &&
                  valGroupsCopy[groupindex].hardMax === param.hardMax)
              );
              const hardMinConflict = !(
                ((valGroupsCopy[groupindex].hardMin === null ||
                  valGroupsCopy[groupindex].hardMin === "") &&
                  (!hasProperty(param, "hardMin") || param.hardMin === "")) ||
                (hasProperty(param, "hardMin") &&
                  valGroupsCopy[groupindex].hardMin === param.hardMin)
              );
              const fixedConflict = !(
                ((valGroupsCopy[groupindex].fixed === null ||
                  valGroupsCopy[groupindex].fixed === "") &&
                  (!param.customFixed || param.customFixed === "")) ||
                valGroupsCopy[groupindex].fixed === param.customFixed
              );
              if (
                valueConflict ||
                hardMaxConflict ||
                hardMinConflict ||
                fixedConflict
              ) {
                includeModelID = true;
                modelsOk = false;
                model.recParams[i][j] = {
                  ...model.recParams[i][j],
                  value: valGroupsCopy[groupindex].value,
                  hardMax:
                    valGroupsCopy[groupindex].hardMax != null
                      ? valGroupsCopy[groupindex].hardMax
                      : "",
                  hardMin:
                    valGroupsCopy[groupindex].hardMin != null
                      ? valGroupsCopy[groupindex].hardMin
                      : "",
                };
                if (valGroupsCopy[groupindex].fixed != null) {
                  model.recParams[i][j] = {
                    ...model.recParams[i][j],
                    customFixed: valGroupsCopy[groupindex].fixed,
                  };
                }
                model.recTableRows[i] = {
                  ...model.recTableRows[i],
                  [param.name]: valGroupsCopy[groupindex].value,
                };
                if (hasProperty(clashingGroups, param.group)) {
                  clashingGroups = {
                    ...clashingGroups,
                    [param.group]: {
                      ...clashingGroups[param.group],
                      memberCount: clashingGroups[param.group].memberCount + 1,
                    },
                  };
                } else {
                  clashingGroups = {
                    ...clashingGroups,
                    [param.group]: {
                      currentValue: valGroupsCopy[groupindex].value,
                      incomingValue: param.value,
                      currentHardMax: valGroupsCopy[groupindex].hardMax,
                      incomingHardMax: param.hardMax,
                      currentHardMin: valGroupsCopy[groupindex].hardMin,
                      incomingHardMin: param.hardMin,
                      currentFixed: valGroupsCopy[groupindex].fixed,
                      incomingFixed: param.customFixed,
                      memberCount: 1,
                      // Track which properties are conflicting
                      conflicts: {
                        value: valueConflict,
                        hardMax: hardMaxConflict,
                        hardMin: hardMinConflict,
                        fixed: fixedConflict,
                      },
                    },
                  };
                }
              }
            }
          }
        }
      }
    }

    if (hasProperty(model, "lineshapeData")) {
      for (let i = 0; i < model.lineshapeData.length; i++) {
        if (hasProperty(model.lineshapeData[i], "lineParams")) {
          for (let j = 0; j < model.lineshapeData[i].lineParams.length; j++) {
            for (
              let k = 0;
              k < model.lineshapeData[i].lineParams[j].length;
              k++
            ) {
              const param = model.lineshapeData[i].lineParams[j][k];
              if (hasProperty(param, "group")) {
                const groupindex = valGroupsCopy.findIndex(
                  (group) => group.groupNumber === param.group
                );
                if (hasProperty(valueCounts, param.group)) {
                  valueCounts[param.group] += 1;
                } else {
                  valueCounts[param.group] = 1;
                }
                if (valGroupsCopy[groupindex].value === null) {
                  valGroupsOk = false;
                  valGroupsCopy[groupindex].value = param.value;
                  valGroupsCopy[groupindex].hardMax =
                    hasProperty(param, "hardMax") && param.hardMax !== ""
                      ? param.hardMax
                      : null;
                  valGroupsCopy[groupindex].hardMin =
                    hasProperty(param, "hardMin") && param.hardMin !== ""
                      ? param.hardMin
                      : null;
                  valGroupsCopy[groupindex].fixed = hasProperty(
                    param,
                    "customFixed"
                  )
                    ? param.customFixed
                    : false;
                } else {
                  const valueConflict =
                    param.value !== valGroupsCopy[groupindex].value;
                  const hardMaxConflict = !(
                    ((valGroupsCopy[groupindex].hardMax === null ||
                      valGroupsCopy[groupindex].hardMax === "") &&
                      (!hasProperty(param, "hardMax") ||
                        param.hardMax === "")) ||
                    (hasProperty(param, "hardMax") &&
                      valGroupsCopy[groupindex].hardMax === param.hardMax)
                  );
                  const hardMinConflict = !(
                    ((valGroupsCopy[groupindex].hardMin === null ||
                      valGroupsCopy[groupindex].hardMin === "") &&
                      (!hasProperty(param, "hardMin") ||
                        param.hardMin === "")) ||
                    (hasProperty(param, "hardMin") &&
                      valGroupsCopy[groupindex].hardMin === param.hardMin)
                  );
                  const fixedConflict = !(
                    ((valGroupsCopy[groupindex].fixed === null ||
                      valGroupsCopy[groupindex].fixed === "") &&
                      (!param.customFixed || param.customFixed === "")) ||
                    valGroupsCopy[groupindex].fixed === param.customFixed
                  );
                  if (
                    valueConflict ||
                    hardMaxConflict ||
                    hardMinConflict ||
                    fixedConflict
                  ) {
                    includeModelID = true;
                    modelsOk = false;
                    model.lineshapeData[i].lineParams[j][k] = {
                      ...model.lineshapeData[i].lineParams[j][k],
                      value: valGroupsCopy[groupindex].value,
                      hardMax:
                        valGroupsCopy[groupindex].hardMax != null
                          ? valGroupsCopy[groupindex].hardMax
                          : "",
                      hardMin:
                        valGroupsCopy[groupindex].hardMin != null
                          ? valGroupsCopy[groupindex].hardMin
                          : "",
                    };
                    if (valGroupsCopy[groupindex].fixed != null) {
                      model.lineshapeData[i].lineParams[j][k] = {
                        ...model.lineshapeData[i].lineParams[j][k],
                        customFixed: valGroupsCopy[groupindex].fixed,
                      };
                    }
                    if (hasProperty(model.lineshapeData[i], "lineTableRows")) {
                      model.lineshapeData[i].lineTableRows[j] = {
                        ...model.lineshapeData[i].lineTableRows[j],
                        [param.name]: valGroupsCopy[groupindex].value,
                      };
                    }
                    if (hasProperty(clashingGroups, param.group)) {
                      clashingGroups = {
                        ...clashingGroups,
                        [param.group]: {
                          ...clashingGroups[param.group],
                          memberCount:
                            clashingGroups[param.group].memberCount + 1,
                        },
                      };
                    } else {
                      clashingGroups = {
                        ...clashingGroups,
                        [param.group]: {
                          currentValue: valGroupsCopy[groupindex].value,
                          incomingValue: param.value,
                          currentHardMax: valGroupsCopy[groupindex].hardMax,
                          incomingHardMax: param.hardMax,
                          currentHardMin: valGroupsCopy[groupindex].hardMin,
                          incomingHardMin: param.hardMin,
                          currentFixed: valGroupsCopy[groupindex].fixed,
                          incomingFixed: param.customFixed,
                          memberCount: 1,
                          // Track which properties are conflicting
                          conflicts: {
                            value: valueConflict,
                            hardMax: hardMaxConflict,
                            hardMin: hardMinConflict,
                            fixed: fixedConflict,
                          },
                        },
                      };
                    }
  
                  }
                }
              }
            }
          }
        }
      }
    }

    if (includeModelID) {
      updatedIds.push(model.FE_ID);
    }

    if (model.subModels.length > 0) {
      model.subModels.forEach((model) => {
        handleDeepGroups(model);
      });
    }
  }
}

/**
 * This function takes in a list of groups and a model, it updates that list with values from the model.
 * This function is used for laoding in models from files and checking if they have clashing groups to the
 * groups in the context.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} groups - This is a list of groups, not necessarily from the context.
 * @param {Object} model - A model that we want to extract group information from.
 * @returns {Array.<Object>} Returns a list of groups with values from the model.
 */
export function produceGroupListWithModelGroups(groups, model) {
  if (model !== null) {
    let copyOfGroups = deepCopy(groups);

    for (let i = 0; i < model.modelParams.length; i++) {
      const param = model.modelParams[i];

      if (hasProperty(param, "group")) {
        copyOfGroups = copyOfGroups.map((group) => {
          if (group.groupNumber === param.group && group.value === null) {
            return { ...group, value: param.value };
          }
          return group;
        });
      }
    }

    if (hasProperty(model, "recParams")) {
      for (let j = 0; j < model.recParams.length; j++) {
        const paramRow = model.recParams[j];

        for (let i = 0; i < paramRow.length; i++) {
          const param = paramRow[i];

          if (hasProperty(param, "group")) {
            copyOfGroups = copyOfGroups.map((group) => {
              if (group.groupNumber === param.group && group.value === null) {
                return { ...group, value: param.value };
              }
              return group;
            });
          }
        }
      }
    }

    if (hasProperty(model, "lineshapeData")) {
      for (let i = 0; i < model.lineshapeData.length; i++) {
        if (hasProperty(model.lineshapeData[i], "lineParams")) {
          for (let j = 0; j < model.lineshapeData[i].lineParams.length; j++) {
            for (
              let k = 0;
              k < model.lineshapeData[i].lineParams[j].length;
              k++
            ) {
              const param = model.lineshapeData[i].lineParams[j][k];
              if (hasProperty(param, "group")) {
                copyOfGroups = copyOfGroups.map((group) => {
                  if (
                    group.groupNumber === param.group &&
                    group.value === null
                  ) {
                    return { ...group, value: param.value };
                  }
                  return group;
                });
              }
            }
          }
        }
      }
    }
    return copyOfGroups;
  } else {
    return null;
  }
}

/**
 * This function takes in a string and returns a shortened version of it if it is longer than 20 characters.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} name - String to be shortened.
 * @param {number} MAX_NAME_LENGTH - Maximum name length number.
 * @returns {string} Returns a shortened string.
 */
export function getShortName(name, MAX_NAME_LENGTH = 20) {
  return name.length > MAX_NAME_LENGTH
    ? name.slice(0, MAX_NAME_LENGTH - 3) + "..."
    : name;
}

/**
 * This function takes in a string and a maximum length, and returns either the original string
 * or a JSX element with a shortened string and tooltip if the string is too long.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} text - The text to potentially shorten.
 * @param {number} maxLength - Maximum length before shortening.
 * @returns {string|JSX.Element} Returns either the original string or a JSX element with tooltip.
 */
export function getTextWithTooltip(text, maxLength) {
  if (text.length > maxLength) {
    return (
      <HtmlTooltip
        title={
          <React.Fragment>
            <div className="customTooltip">{text}</div>
          </React.Fragment>
        }
      >
        <span>{getShortName(text, maxLength)}</span>
      </HtmlTooltip>
    );
  }
  return text;
}

/**
 * This function takes in an array of ranges and checks if any of those ranges wrap zero. This is used in
 * checking if the user did the 0 exlusion himself when automatic exlusion is off.
 * @function
 * @memberof Utilities.Helpers
 * @param {Array.<Object>} arr - This is an array of ranges.
 * @returns {boolean} Returns true if any of the ranges in the array wrap zero, false otherwise.
 */
export function doesAnyRangeWrapZero(arr) {
  for (let i = 0; i < arr.length; i++) {
    const element = arr[i];

    if (element.min === "" || element.max === "") {
      continue;
    }

    if (element.min <= 0 && element.max >= 0) {
      return true;
    }
  }
  return false;
}

const h = 4.135667516e-15;
const c = 29979245800;

/**
 * This function translates a number given in cm-1 to eV.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in eV.
 */
export function cm1ToeV(value) {
  return value * h * c;
}
/**
 * This function translates a number given in eV to cm-1.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in cm-1.
 */
export function eVTocm1(value) {
  return value / (h * c);
}

/**
 * This function translates a number given in cm-1 to THz.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in THz.
 */
export function cm1ToTHz(value) {
  return value * c * 1e-12;
}

/**
 * This function translates a number given in THz to cm-1.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in cm-1.
 */
export function THzTocm1(value) {
  return (value * 1e12) / c;
}

/**
 * This function translates a number given in cm-1 to nm.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in nm.
 */
export function cm1Tonm(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 10000000 / value;
}

/**
 * This function translates a number given in nm to cm-1.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in cm-1.
 */
export function nmTocm1(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 10000000 / value;
}

/**
 * This function translates a number given in cm-1 to microns.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in microns.
 */
export function cm1toMicrons(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 10000 / value;
}

/**
 * This function translates a number given in microns to cm-1.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in cm-1.
 */
export function micronsTocm1(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 10000 / value;
}

/**
 * This function translates a number given in cm-1 to ps.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in microns.
 */
export function cm1tops(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 1e12 / c / value;
}

/**
 * This function translates a number given in ps to cm-1.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in cm-1.
 */
export function psTocm1(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 1e12 / c / value;
}

/**
 * This function translates a number given in cm-1 to mm.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in mm.
 */
export function cm1Tomm(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 10 / value;
}

/**
 * This function translates a number given in mm to cm-1.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - Numeric value to be converted.
 * @returns {number} Returns a number that represents the value in cm-1.
 */
export function mmTocm1(value) {
  if (value === 0) {
    value = 1e-30;
  }
  return 10 / value;
}

/**
 * This function takes in a string and returns true if it is in a correct email syntax
 * @function
 * @memberof Utilities.Helpers
 * @param {string} email - String representing email
 * @returns {boolean} Returns true if email is in correct format
 */
export function validateEmail(email) {
  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

/**
 * This function takes in date string in ISO 8601 format and returns it in a more readable format
 * @function
 * @memberof Utilities.Helpers
 * @param {string} dateString - This string represents date string in ISO 8601 forma
 * @returns {string} This function returns string in DD-MM-YYYY HH:mm format
 */
export function formatDate(dateString) {
  // Create a Date object from the input string
  const date = new Date(dateString);

  // Get the day, month, and year
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // January is 0!
  const year = date.getFullYear();

  // Get the hours and minutes
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  // Format the string
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * This function takes in a date and returns if it's in the future or not. Used for determining if poll
 * is still active.
 * @function
 * @memberof Utilities.Helpers
 * @param {string} dateString - This is ISO 8601 format date string
 * @returns {boolean} This function returns true if given date is in the future, false otherwise
 */
export function isActive(dateString) {
  // Parse the given date string and current date to Date objects
  const givenDate = new Date(dateString);
  const currentDate = new Date();

  // Remove milliseconds for precise comparison to the second
  givenDate.setMilliseconds(0);
  currentDate.setMilliseconds(0);

  // Compare the two dates
  if (givenDate > currentDate) {
    return true;
  } else {
    return false;
  }
}

/**
 * This function takes in a part and total numeric values and return how many percent is part of the total.
 * @function
 * @memberof Utilities.Helpers
 * @param {number} part - This is a number that we want to know the percentage of.
 * @param {number} total - This is a total number of counts that represents 100%.
 * @returns {number} Returns a numeric value that represents percentage of how much part is of total.
 */
export function calculatePercentage(part, total) {
  // Check for invalid inputs: part is 0 or any number is negative
  if (part === 0 || part < 0 || total <= 0) {
    return 0;
  }

  // Calculate the percentage
  const percentage = (part / total) * 100;

  // Return the percentage value formatted to 2 numbers after comma
  return percentage.toFixed(2);
}

/**
 * This function takes in a number that represents temperature and a string that shows which temperature
 * unit is being used, then it returns the value in Kelvin
 * @function
 * @memberof Utilities.Helpers
 * @param {number} value - This value represents numeric temperature value
 * @param {string} unit - This is a single character string that represets temperature unit
 * @returns {number} This returns a numeric temperature representation in Kelvin
 */
export function convertToKelvin(value, unit) {
  let kelvin;
  switch (unit.toUpperCase()) {
    case "K":
      kelvin = value;
      break;
    case "C":
      kelvin = value + 273.15;
      break;
    case "F":
      kelvin = (value - 32) * (5 / 9) + 273.15;
      break;
    default:
      throw new Error(
        "Invalid temperature unit. Use 'K' for Kelvin, 'C' for Celsius, or 'F' for Fahrenheit."
      );
  }
  return kelvin;
}

/**
 * This function handles file reading into manageable format. If file cannot be readed for some reason, it rejects
 * with an error. This is different from fileProcessingLogic function in a way that it generates no warnings and
 * leaves with the caller of this function to handle errors related to file loading.
 * @function
 * @memberof Utilities.Helpers
 * @param {file} file - File loaded.
 * @returns {Promise} - Returns a promise that resolves in file reading response.
 */
export function simpleReadFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const fileData = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        content: event.target.result,
      };
      resolve(fileData);
    };
    reader.onerror = () => {
      reader.abort();
      reject(new Error("Error reading the file."));
    };
    // if (
    //   file.name.endsWith(".rfm") ||
    //   file.name.endsWith(".RFM") ||
    //   file.name.endsWith(".rfv") ||
    //   file.name.endsWith(".RFV")
    // ) {
    //   reader.readAsArrayBuffer(file);
    // } else {
    reader.readAsText(file);
    //    }
  });
}

/**
 * This function takes in unit and value and returns that value in cm^-1
 * @function
 * @memberof Utilities.Helpers
 * @param {Object} unit - This variable represents the units in which the value is given
 * @param {number} value - This is the numeric value that we want to change to cm^-1
 * @returns {number} This is value translated into cm^-1
 */
export function anyUnitToCm1(unit, value) {
  let newValue = value;

  switch (unit.name) {
    case "cm^-1":
      break;
    case "eV":
      newValue = eVTocm1(newValue);
      break;
    case "THz":
      newValue = THzTocm1(newValue);
      break;
    case "nm":
      newValue = nmTocm1(newValue);
      break;
    case "microns":
      newValue = micronsTocm1(newValue);
      break;
    case "ps":
      newValue = psTocm1(newValue);
      break;
    case "mm":
      newValue = mmTocm1(newValue);
      break;
    default:
      break;
  }

  return newValue;
}

/**
 * This function takes value in cm^-1 and returns that value in unit requested
 * @function
 * @memberof Utilities.Helpers
 * @param {Object} unit - This variable represents the units we need to convert to
 * @param {number} value - This is the numeric value in cm^-1
 * @returns {number} This is value translated into requested units
 */
export function cm1ToAnyUnit(unit, value) {
  let newValue = value;

  switch (unit.name) {
    case "cm^-1":
      break;
    case "eV":
      newValue = cm1ToeV(newValue);
      break;
    case "THz":
      newValue = cm1ToTHz(newValue);
      break;
    case "nm":
      newValue = cm1Tonm(newValue);
      break;
    case "microns":
      newValue = cm1toMicrons(newValue);
      break;
    case "ps":
      newValue = cm1tops(newValue);
      break;
    case "mm":
      newValue = cm1Tomm(newValue);
      break;
    default:
      break;
  }

  return newValue;
}

export function isInverseUnit(unit) {
  if (
    unit.value === "nm" ||
    unit.value === "microns" ||
    unit.value === "ps" ||
    unit.value === "mm"
  ) {
    return true;
  }
  return false;
}

/**
 * This function takes in information about models and which model-quantity pair we want to check and returns
 * a name associated to that quantity.
 * @function
 * @param {Array.<Object>} modelList - This is a list of all the models used in app at the moment
 * @param {number} modelId - This number represents ID of the model we wan to check
 * @param {number} quantity - This number represents quantity of the model we are checking
 * @param {boolean} noProcess - This flag decides if we want to do string processing before returning
 * @returns {string} This returns quantity name
 */
export function getQuantityNameFromModels(
  modelList,
  modelId,
  quantity,
  noProcess = false
) {
  const model = getModelById(modelId, modelList);

  if (model && hasProperty(model, "outputs") && model.outputs.length > 0) {
    for (let i = 0; i < model.outputs.length; i++) {
      const output = model.outputs[i];

      if (output.speqqle_id === quantity) {
        if (noProcess) {
          return output.name;
        } else {
          return processSymbols(output.name);
        }
      }
    }
  }

  return "no_outputs";
}

export function getQuantityXUnitFromModels(modelList, modelId, quantity) {
  let xUnit = units[0];
  const model = getModelById(modelId, modelList);

  if (model && hasProperty(model, "outputs") && model.outputs.length > 0) {
    for (let i = 0; i < model.outputs.length; i++) {
      const output = model.outputs[i];

      // Use == instead of === to handle type coercion (quantity might be number or string)
      if (
        output.speqqle_id == quantity &&
        hasProperty(output, "xunits") &&
        output.xunits !== "cm-1"
      ) {
        // Check for both lowercase and uppercase property names
        const xunitValue = output.xunits;

        xUnit = units.find((unit) => unit.value === xunitValue);
        if (!xUnit) {
          xUnit = units[0];
        }
        break; // Found the matching output, no need to continue
      }
    }
  }
  return xUnit;
}

/**
 * Adjusts modal position and size to ensure it fits within the viewport.
 * @function
 * @param {Object} desiredPosition - The desired position with optional 'top', 'left', or 'right' ({ top, left, right }).
 * @param {Object} size - Initial width and height of the modal ({ width, height }).
 * @returns {Object} - Adjusted position and size including 'top' and either 'left' or 'right', formatted with "px".
 */
export function adjustModalPositionAndSize(desiredPosition, size) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let { top, left, right } = desiredPosition;
  let { width, height } = size;

  // Adjust size to fit the viewport while not going below 70% of the original size
  let adjustedWidth = Math.min(width, viewportWidth);
  let adjustedHeight = Math.min(height, viewportHeight);

  adjustedWidth = Math.max(adjustedWidth, width * 0.7);
  adjustedHeight = Math.max(adjustedHeight, height * 0.7);

  // Position adjustments
  if (typeof right !== "undefined") {
    if (right !== "auto") {
      right = Math.max(
        0,
        Math.min(viewportWidth - adjustedWidth, parseInt(right, 10))
      );
      right = `${right}px`;
    }
  }
  if (typeof left !== "undefined") {
    if (left !== "auto") {
      left = Math.max(
        0,
        Math.min(viewportWidth - adjustedWidth, parseInt(left, 10))
      );
      left = `${left}px`;
    }
  }
  if (typeof right === "undefined" && typeof left === "undefined") {
    // Default to left 0px if neither left nor right is defined
    left = "0px";
  }

  top = Math.max(
    0,
    Math.min(viewportHeight - adjustedHeight, parseInt(top, 10))
  );
  top = `${top}px`;

  // Ensure string values with 'px' for dimensions
  adjustedWidth = `${adjustedWidth}px`;
  adjustedHeight = `${adjustedHeight}px`;

  return { top, right, left, width: adjustedWidth, height: adjustedHeight };
}

/**
 * Converts viewport width units (vw) to pixels.
 * @function
 * @param {number} vw - The number of viewport width units.
 * @returns {number} - The corresponding pixel value.
 */
export function vwToPixels(vw) {
  return vw * (window.innerWidth / 100);
}

/**
 * Converts viewport height units (vh) to pixels.
 * @function
 * @param {number} vh - The number of viewport height units.
 * @returns {number} - The corresponding pixel value.
 */
export function vhToPixels(vh) {
  return vh * (window.innerHeight / 100);
}

/**
 * Converts percentage of parent width to pixels.
 * @function
 * @param {number} percent - The percentage of the parent element's width.
 * @param {number} parentWidth - The parent element's width in pixels.
 * @returns {number} - The corresponding pixel value.
 */
export function percentWidthToPixels(percent, parentWidth) {
  return (percent / 100) * parentWidth;
}

/**
 * Converts percentage of parent height to pixels.
 * @function
 * @param {number} percent - The percentage of the parent element's height.
 * @param {number} parentHeight - The parent element's height in pixels.
 * @returns {number} - The corresponding pixel value.
 */
export function percentHeightToPixels(percent, parentHeight) {
  return (percent / 100) * parentHeight;
}

/**
 * This function takes in a model and puts its data into csv and downloads it.
 * @function
 * @param {Object} model - This is a model that we want to download.
 */
export function constructAndDownloadCSV(model) {
  const csvRows = [];
  const headers = ["Name", "Recurring", "Unit", "Value"];
  csvRows.push(headers.join(","));

  for (let i = 0; i < model.modelParams.length; i++) {
    const parameter = model.modelParams[i];
    if (parameter.recuring === 0) {
      csvRows.push(`${parameter.name},0,,${parameter.value}`);
    }
  }

  if (hasProperty(model, "recParams")) {
    for (let i = 0; i < model.recParams.length; i++) {
      const parameterRow = model.recParams[i];
      for (let j = 0; j < parameterRow.length; j++) {
        const parameter = parameterRow[j];
        csvRows.push(`${parameter.name},${i + 1},cm^-1,${parameter.value}`);
      }
    }
  }

  const joinedRows = csvRows.join("\n");

  downloadCSV(joinedRows, model.displayName);
}

/**
 * Triggers a download of a CSV file containing the provided data.
 * @function
 * @param {Array.<Object>} data - The array of objects to be converted into CSV format. Each object in the array should have the same structure.
 * @returns - Initiates a download action but does not return any value.
 */
export function downloadCSV(csvData, name) {
  const blob = new Blob([csvData], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${name}.csv`);
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// /**
//  * Converts an ArrayBuffer to a base64 encoded string.
//  * @function
//  * @param {ArrayBuffer} buffer - The ArrayBuffer to be converted to base64 format.
//  * @returns {string} - The base64 encoded string representing the binary data.
//  */
// export const arrayBufferToBase64 = (buffer) => {
//   // Create a binary string from the ArrayBuffer
//   let binary = "";
//   const bytes = new Uint8Array(buffer);

//   const len = bytes.byteLength;
//   for (let i = 0; i < len; i++) {
//     binary += String.fromCharCode(bytes[i]);
//   }

//   // Encode the binary string to base64
//   return window.btoa(binary);
// };

/**
 * This function takes in and id list of models with supporting data and returns a dictionary of model and quantity pairs
 * @function
 * @param {Array.<number>} idList - This is a list of ids that we want to generate a dictionary for
 * @param {Object} rangesForModels - This is a dictionary for what model and what quantity has what ranges
 * @param {Array.<Object>} modelData - This is all the models contained in the app with their data
 * @returns {Object} Returns an object with models that were requested and quantity pairs
 */
export function generateModelsRangesQuantPairsForModelDist(
  idList,
  rangesForModels,
  modelData
) {
  let models = [];
  let quantityPairs = [];

  for (let i = 0; i < idList.length; i++) {
    const idToFind = idList[i];
    const foundModel = getModelById(idToFind, modelData);

    if (hasProperty(rangesForModels, idToFind)) {
      const allQuantities = Object.keys(rangesForModels[idToFind]);
      for (let k = 0; k < allQuantities.length; k++) {
        const quantity = allQuantities[k];
        if (!models.some((model) => model.FE_ID === foundModel.FE_ID)) {
          models.push(foundModel);
        }
        quantityPairs.push({
          modelId: foundModel.FE_ID,
          quantity: parseInt(quantity),
        });
      }
    }
  }

  return {
    models,
    quantityPairs,
  };
}

/**
 * This function takes in a list of model ids and model data and returns those ids with added sub model ids.
 * @function
 * @param {Array.<number>} modelIdList - This is a list of ids that we want to check
 * @param {Array.<Object>} modelData - These are all the models contained in the app with their data
 * @returns {Array.<number>} This function returns all the ids that requested array contained plus the ids of sub models.
 *  This array has no duplicates
 */
export function getAllIdsForModelsAndSubmodels(modelIdList, modelData) {
  const allIds = [];

  for (let i = 0; i < modelIdList.length; i++) {
    const id = modelIdList[i];
    const matchingModels = getModelsByIds([id], modelData);

    for (let j = 0; j < matchingModels.length; j++) {
      const model = matchingModels[j];

      addIdToList(model);
    }
  }

  const uniqueIds = [...new Set(allIds)];

  return uniqueIds;

  function addIdToList(model) {
    allIds.push(model.FE_ID);

    if (hasProperty(model, "subModels") && model.subModels.length > 0) {
      for (let i = 0; i < model.subModels.length; i++) {
        const subModel = model.subModels[i];

        addIdToList(subModel);
      }
    }
  }
}

/**
 * This function finds largest and smallest y axis value from given coordinates
 * @function
 * @param {Array.<{x: number, y: number}>} coordinates - This is an array of coordinates [{x: 1, y: 1}]
 * @returns {Object} This function returns an object that contains information about largest and smallest y axis value.
 */
export function findMinMaxY(coordinates) {
  if (coordinates.length === 0) {
    return { minY: null, maxY: null };
  }

  let minY = coordinates[0].y;
  let maxY = coordinates[0].y;

  for (let i = 1; i < coordinates.length; i++) {
    if (coordinates[i].y < minY) {
      minY = coordinates[i].y;
    }
    if (coordinates[i].y > maxY) {
      maxY = coordinates[i].y;
    }
  }

  return { minY: minY, maxY: maxY };
}

/**
 * This function checks if the input string is a complete and valid number.
 * @function
 * @param {string} value - A string that is checked if it is a complete and valid number.
 * @returns {boolean} Returns true if the input string is a complete and valid number, false otherwise.
 */
export function isCompleteNumber(value) {
  // Regular expression to check if the string is a valid complete number
  const validNumberRegex = /^[-+]?\d+(\.\d+)?([Ee][+-]?\d+)?$/;

  // If the input matches the valid number regex, it is a complete number
  if (validNumberRegex.test(value)) {
    return true;
  }

  // Otherwise, it is incomplete
  return false;
}

// export function removeModelsFromTablesById(
//   modelList,
//   idToRemove,
//   affectedParentIDs = []
// ) {
//   const updatedList = modelList.map((model) => {
//     if (!model.canAdd) {
//       return model;
//     }

//     let indexOfFoundRow = -1;

//     for (let i = 0; i < model.recParams.length; i++) {
//       const paramRow = model.recParams[i];
//       if (
//         paramRow.some(
//           (param) => param.type === "Model" && param.FE_ID === idToRemove
//         )
//       ) {
//         indexOfFoundRow = i;
//         break;
//       }
//     }

//     if (indexOfFoundRow !== -1) {
//       model.recTableRows.splice(indexOfFoundRow, 1);
//       model.recParams.splice(indexOfFoundRow, 1);

//       // If this model's tables were affected, add its ID to the affectedParentIDs list
//       if (!affectedParentIDs.includes(model.FE_ID)) {
//         affectedParentIDs.push(model.FE_ID);
//       }
//     }

//     if (model.subModels.length > 0) {
//       model.subModels = removeModelsFromTablesById(
//         model.subModels,
//         idToRemove,
//         affectedParentIDs
//       );
//     }

//     return model;
//   });

//   return updatedList;
// }

export function checkBool(value) {
  switch (value) {
    case 1:
      return true;
    case 0:
      return false;
    default:
      return value;
  }
}

export function produceListOfExchangeableModels(
  modelData,
  linkingID,
  linkingSpeqqleID,
  indexChain = null
) {
  const optionsForLinking = [];

  const checkAndPushOptions = (model) => {
    if (
      linkingID !== model.FE_ID &&
      canLink(
        modelData,
        linkingID,
        model.FE_ID,
        linkingSpeqqleID,
        model.speqqleID
      ) &&
      !optionsForLinking.some((option) => option.FE_ID === model.FE_ID)
    ) {
      optionsForLinking.push({
        title: model.displayName,
        FE_ID: model.FE_ID,
        speqqleID: linkingSpeqqleID,
        indexChain: indexChain,
      });
    }
    if (model.subModels.length > 0) {
      for (let i = 0; i < model.subModels.length; i++) {
        const subMod = model.subModels[i];
        checkAndPushOptions(subMod);
      }
    }
  };

  for (let i = 0; i < modelData.length; i++) {
    const model = modelData[i];
    checkAndPushOptions(model);
  }

  return optionsForLinking;
}

function canLink(modelData, linkingID, ID, linkingSpeqqleID, speqqleID) {
  if (
    hasParentOrChildWithID(modelData, linkingID, speqqleID, ID) ||
    linkingID === ID ||
    speqqleID !== linkingSpeqqleID
  ) {
    return false;
  } else {
    return true;
  }
}

export function djb2Hash(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i); // hash * 33 + c
  }
  return hash >>> 0; // Ensure positive integer
}

/**
 * This function takes in a list of model FE_IDs and checks which of those models are contained in graphs, then
 * it returns a lsit of pairs of those contained models with their quantities.
 * @param {Array.<number>} modelsIds - This array contains FE_IDs of the models that interest us in graphs
 * @param {Array.<Object>} graphs - This is current graph array
 * @returns This function returns a list of pairs of model ids and their quantities, no duplicates.
 */
export function checkModelsInGraphs(modelsIds, graphs) {
  const allMergedModels = [];
  let dictionary = {};
  const pairList = [];

  for (let i = 0; i < graphs.length; i++) {
    const graph = graphs[i];

    allMergedModels.push(...graph.containedModels);
  }

  for (let i = 0; i < allMergedModels.length; i++) {
    const modelEntry = allMergedModels[i];

    if (modelsIds.some((id) => id === modelEntry.modelId)) {
      if (hasProperty(dictionary, modelEntry.modelId)) {
        const newQuantList = [
          ...new Set([...dictionary[modelEntry.modelId], modelEntry.quantity]),
        ];
        dictionary = {
          ...dictionary,
          [modelEntry.modelId]: newQuantList,
        };
      } else {
        dictionary = {
          ...dictionary,
          [modelEntry.modelId]: [modelEntry.quantity],
        };
      }
    }
  }

  const containedModels = Object.keys(dictionary);
  const parsedModelIds = [];

  // We are doing this instead of just returning all merged models is because we avoid duplicate pairs and we get
  // only models that we asked for.
  for (let i = 0; i < containedModels.length; i++) {
    const modelId = parseInt(containedModels[i]);

    parsedModelIds.push(modelId);

    for (let j = 0; j < dictionary[modelId].length; j++) {
      const quantity = dictionary[modelId][j];

      pairList.push({ modelId: modelId, quantity: quantity });
    }
  }

  return {
    pairs: pairList,
    modelList: parsedModelIds,
  };
}

export function checkIfAllowedActionRequest(message) {
  if (
    hasProperty(message, "stopfit") ||
    hasProperty(message, "stop_task") ||
    hasProperty(message, "pause_next_iteration") ||
    hasProperty(message, "stop_pause")
  ) {
    return true;
  }
  return false;
}

/**
 * Converts a positive real number to its log10 exponent.
 *
 * For example:
 *   - normalToLog(1)   -> 0
 *   - normalToLog(25)  -> ~1.39794
 *   - normalToLog(100) -> 2
 *
 * @param {number} value A positive number.
 * @return {number} The base-10 log exponent.
 */
export function normalToLog(value) {
  if (value <= 0) {
    throw new Error("Value must be greater than 0 for a log scale.");
  }
  return Math.log10(value);
}

/**
 * Converts a base-10 log exponent to a normal (linear) number.
 *
 * Examples:
 *   - logToNormal(0)      -> 1
 *   - logToNormal(1)      -> 10
 *   - logToNormal(-1)     -> 0.1
 *   - logToNormal(1.39794)-> ~25
 *
 * @param {number} logValue The base-10 log exponent.
 * @return {number} The corresponding linear (normal) value.
 */
export function logToNormal(logValue) {
  return Math.pow(10, logValue);
}

/**
 * Converts JSX elements produced by processSymbols into HTML strings that Plotly can render
 * @function
 * @param {Array|string} jsxElements - Array of strings and JSX elements or a single string
 * @returns {string} - HTML string with proper subscript and superscript tags
 */
export function jsxToHtmlString(jsxElements) {
  if (!jsxElements || typeof jsxElements === "string") {
    return jsxElements || "";
  }

  return jsxElements
    .map((element) => {
      if (typeof element === "string") {
        return element;
      } else if (React.isValidElement(element)) {
        const type = element.type;
        const content = element.props.children;

        if (type === "sub") {
          return `<sub>${content}</sub>`;
        } else if (type === "sup") {
          return `<sup>${content}</sup>`;
        }

        return content;
      }
      return "";
    })
    .join("");
}

// /**
//  * This function takes in a list of models, list of ids to check and id to skip. It checks if provided list
//  * of model ids are linked somewhere else besides given 'idToExcept'. This is useful to check if sub models
//  * are linked and used somewhere else other than under the parent model.
//  * @function
//  * @param {Array.<Object>} modelList This is the model list in which we check linked status
//  * @param {Array<number>} idsToCheck This is a list of IDs that we check if they are linked
//  * @param {number} idToExcept This is an ID that we skip in checking if linked
//  * @returns {Object} - This returns a dictionary of IDs as keys and booleans as values
//  */
// export function getModelsLinkedExceptGiven(modelList, idsToCheck, idToExcept) {
//   const linkedDict = {};

//   idsToCheck.forEach((id) => (linkedDict[id] = false));

//   const checkModelList = (list) => {
//     for (let i = 0; i < list.length; i++) {
//       const model = list[i];

//       if (model.FE_ID === idToExcept) {
//         continue;
//       }

//       if (hasProperty(linkedDict, model.FE_ID) && model.linked) {
//         linkedDict[model.FE_ID] = true;
//       }

//       if (model.subModels.length > 0) {
//         checkModelList(model.subModels);
//       }
//     }
//   };

//   checkModelList(modelList);

//   return linkedDict;
// }

export function updateFileDataAfterOptionChange(
  fileData,
  dataPoints,
  optionObject,
  defaultXAxisUnit
) {
  // Calculate exact min and max values without rounding
  const maxX = dataPoints.reduce(function (max, p) {
    return p.x > max ? p.x : max;
  }, dataPoints[0].x);

  const minX = dataPoints.reduce(function (min, p) {
    return p.x < min ? p.x : min;
  }, dataPoints[0].x);

  fileData.dataPoints = dataPoints;
  fileData.npoints = dataPoints.length;
  fileData.dataRangeMin = minX;
  fileData.dataRangeMax = maxX;

  // Reset all edges when file options change (in case original file units were set wrongly)
  fileData.edges = [{ min: "", max: "", unit: defaultXAxisUnit }];
  fileData.options = optionObject;
  fileData.xUnit = optionObject.xUnit;

  return deepCopy(fileData);
}

/**
 * This function takes in array of data points and options that were applied to the said data points and
 * then reverts such options to original points
 * @function
 * @param {Array.<{x: number, y: number}>} dataPoints This is an array of data points that have 'x' and 'y' coordinates
 * @param {Object} options This is an options object that defines changes done to the data points
 * @returns {Array.<{x: number, y: number}>} This returns an array of original data points
 */
export function revertToOriginalPoints(dataPoints, options) {
  if (!options.shift) {
    return dataPoints;
  }

  const revertedPoints = dataPoints.map((point) => {
    let revertedX = point.x;
    let revertedY = point.y;

    if (options.xShift !== "none") {
      const xShiftVal = parseFloat(options.xShiftVal);

      // Convert to shift unit before applying reverse shift
      if (options.xShiftUnit) {
        revertedX = cm1ToAnyUnit(options.xShiftUnit, revertedX);
      }

      switch (options.xShift) {
        case "val-x":
          revertedX = xShiftVal - revertedX;
          break;
        case "x+val":
          revertedX = revertedX - xShiftVal;
          break;
        case "x-val":
          revertedX = revertedX + xShiftVal;
          break;
        case "x*val":
          revertedX = xShiftVal !== 0 ? revertedX / xShiftVal : revertedX;
          break;
        default:
          break;
      }

      // Convert back from shift unit to cm⁻¹
      if (options.xShiftUnit) {
        revertedX = anyUnitToCm1(options.xShiftUnit, revertedX);
      }
    }
    if (options.yShift !== "none") {
      const yShiftVal = parseFloat(options.yShiftVal);
      switch (options.yShift) {
        case "val-x":
          revertedY = yShiftVal - revertedY;
          break;
        case "x+val":
          revertedY = revertedY - yShiftVal;
          break;
        case "x-val":
          revertedY = revertedY + yShiftVal;
          break;
        case "x*val":
          revertedY = yShiftVal !== 0 ? revertedY / yShiftVal : revertedY;
          break;
        default:
          break;
      }
    }

    return { x: revertedX, y: revertedY };
  });

  // Sort points by x value (smallest first)
  return revertedPoints;
}

export function getPlaceholderParamWindowPosition(posIndex) {
  const DEFAULT_DIMENSION = 200;
  const GAP = 15;
  const window_width = window.innerWidth;
  const isFirst = posIndex % 2 === 0;
  const row = Math.floor(posIndex / 2);

  let xPos = DEFAULT_DIMENSION + GAP;

  if (isFirst) {
    xPos = xPos + DEFAULT_DIMENSION + GAP;
  }

  xPos = window_width - xPos;

  let yPos = row * DEFAULT_DIMENSION + row * GAP;

  return { x: xPos, y: yPos };
}

/**
 * Lays out windows so that they don't overlap.
 * Places at most 2 windows per row, but if a window is very wide
 * (its width > 30% of innerWidth), it is placed alone.
 *
 * This function updates the original windows object so that the new
 * position is stored under each window's "position" key, preserving other keys.
 * It ensures that no window, including its gap, is partially outside the viewport.
 *
 * @param {Object} windowsCopy - Object where each key is a window ID and each value has "position" and "size" properties.
 * @param {number} innerWidth - The current window inner width.
 * @returns {Object} windows - The updated windows object with recalculated positions.
 */
export function getLayoutForWindows(windows, innerWidth) {
  const GAP = 15;
  const rows = [];
  let currentRow = [];

  let windowsCopy = deepCopy(windows);

  // Sort window IDs for predictable ordering (assumed numeric keys).
  const windowIds = Object.keys(windowsCopy).sort(
    (a, b) => parseInt(a) - parseInt(b)
  );

  // Group windows into rows.
  for (const id of windowIds) {
    const { width } = windowsCopy[id].size;
    // If the window is very wide, place it in its own row.
    if (width > innerWidth * 0.45) {
      rows.push([id]);
    } else {
      currentRow.push(id);
      if (currentRow.length === 2) {
        rows.push(currentRow);
        currentRow = [];
      }
    }
  }
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  // Compute positions row by row.
  let yPos = 0;
  for (const row of rows) {
    let rowHeight = 0;
    for (const id of row) {
      const { height } = windowsCopy[id].size;
      rowHeight = Math.max(rowHeight, height);
    }
    // if (row.length === 2) {
    //   totalRowWidth += GAP;
    // }

    // For a single window row, position it flush to the right (but ensure it doesn't overflow).
    if (row.length === 1) {
      const id = row[0];
      const w = windowsCopy[id].size.width;
      let startX = innerWidth - w;
      // Clamp to 0 if the window is wider than the inner width.
      startX = Math.max(startX, 0);
      windowsCopy[id].position = { x: startX, y: yPos };
    }
    // For a row with two windows, check if the standard layout fits.
    else if (row.length === 2) {
      const id1 = row[0],
        id2 = row[1];
      const w1 = windowsCopy[id1].size.width;
      const w2 = windowsCopy[id2].size.width;
      // If the two windows plus gap fit within the innerWidth,
      // position them as far right as possible.
      if (w1 + GAP + w2 <= innerWidth) {
        let startX = innerWidth - (w1 + GAP + w2);
        windowsCopy[id1].position = { x: startX, y: yPos };
        windowsCopy[id2].position = { x: startX + w1 + GAP, y: yPos };
      } else {
        // Otherwise, adjust so that:
        // - The left window starts at x = 0
        // - The right window is flush to the right edge (x + width === innerWidth)
        windowsCopy[id1].position = { x: 0, y: yPos };
        windowsCopy[id2].position = { x: innerWidth - w2, y: yPos };
      }
    }
    // Increase vertical position for next row.
    yPos += rowHeight + GAP;
  }

  return windowsCopy;
}

export function offsetWindowsFromTop(windowGrid, offsetAmount) {
  let updatedGrid = deepCopy(windowGrid);

  const gridKeys = Object.keys(updatedGrid);

  for (let i = 0; i < gridKeys.length; i++) {
    const key = gridKeys[i];
    updatedGrid[key] = {
      ...updatedGrid[key],
      position: {
        ...updatedGrid[key].position,
        y: updatedGrid[key].position.y + offsetAmount,
      },
    };
  }

  return updatedGrid;
}

/**
 * Collects session data for error reports, returns simple message if too large
 * @param {boolean} includeSessionData - Whether to include session data
 * @returns {string} - HTML string with session data or "session too large" message
 */
export function collectSessionDataForErrorReport(includeSessionData) {
  if (!includeSessionData) {
    return "";
  }

  try {
    // Use the same format as the save session functionality
    const dashboardVariables = {};
    const graphVariables = {};

    // Get all sessionStorage keys and organize them by context
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      // Skip sensitive/irrelevant data
      if (
        key === "authToken" ||
        key === "errorLog" ||
        key === "isAdmin" ||
        key === "isActive" ||
        key === "packages" ||
        key === "currentUser" ||
        key === "isFirstLogin" ||
        key === "baseBE_URL"
      )
        continue;

      try {
        const value = window.sessionStorage.getItem(key);
        const parsedValue = JSON.parse(value);

        // Categorize variables based on the save session structure
        if (
          key.includes("file") ||
          key.includes("model") ||
          key.includes("param") ||
          key.includes("data") ||
          key.includes("preset") ||
          key.includes("warning") ||
          key.includes("gauge") ||
          key.includes("unit") ||
          key.includes("range") ||
          key.includes("abbr") ||
          key.includes("naming") ||
          key.includes("group") ||
          key.includes("upload") ||
          key.includes("iteration") ||
          key.includes("active") ||
          key.includes("col") ||
          key.includes("pos") ||
          key.includes("sort") ||
          key === "chiVal" ||
          key === "trackedParameters" ||
          key === "modelsZIndices"
        ) {
          dashboardVariables[key] = parsedValue;
        } else if (
          key.includes("graph") ||
          key === "chi2Terms" ||
          key === "chi2TermID" ||
          key === "selectedChi2Terms"
        ) {
          graphVariables[key] = parsedValue;
        } else {
          // For any other keys, add to dashboard as general session data
          dashboardVariables[key] = parsedValue;
        }
      } catch (parseError) {
        // If value can't be parsed as JSON, store as string
        const value = window.sessionStorage.getItem(key);
        dashboardVariables[key] = value;
      }
    }

    const combinedVariables = {
      dashboard: dashboardVariables,
      graph: graphVariables,
    };

    return `<h4>--- SESSION DATA ATTACHED (Same Format as Save Session) ---</h4>
    <pre>${JSON.stringify(combinedVariables, null, 2)}</pre>`;
  } catch (sessionError) {
    // Fallback to simple key-value format if the structured approach fails
    let sessionDataLines = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (
        key === "authToken" ||
        key === "errorLog" ||
        key === "isAdmin" ||
        key === "isActive" ||
        key === "packages" ||
        key === "currentUser" ||
        key === "isFirstLogin" ||
        key === "baseBE_URL"
      )
        continue;
      const value = window.sessionStorage.getItem(key);
      sessionDataLines.push(
        `<p><strong>session[${key}]:</strong> ${value}</p>`
      );
    }
    if (sessionDataLines.length > 0) {
      return `<h4>--- SESSION DATA ATTACHED (Fallback Format) ---</h4>${sessionDataLines.join(
        ""
      )}`;
    }
    return "";
  }
}

/**
 * Safely sets a value in sessionStorage with quota error handling
 * @param {string} key - The storage key
 * @param {string} value - The value to store
 * @param {function} cleanupCallback - Optional callback to clean up unsaved data when quota is exceeded
 * @param {function} setWarnings - Optional function to set warnings (for quota monitoring)
 * @param {function} setNewWarningCount - Optional function to set warning count (for quota monitoring)
 * @returns {boolean} - True if successful, false if quota exceeded
 */
export function safeSessionStorageSet(
  key,
  value,
  cleanupCallback = null,
  setWarnings = null,
  setNewWarningCount = null
) {
  try {
    window.sessionStorage.setItem(key, value);

    // Check if this is a datafile-related key and monitor quota usage
    if (isDatafileKey(key)) {
      checkQuotaUsage(setWarnings, setNewWarningCount);
    }

    return true;
  } catch (error) {
    if (error.name === "QuotaExceededError") {
      console.warn(`SessionStorage quota exceeded for key: ${key}`);
      alert("Memory quota exceeded, please remove some datafiles.");

      // Call cleanup callback if provided to remove unsaved data
      if (cleanupCallback && typeof cleanupCallback === "function") {
        try {
          cleanupCallback();
        } catch (cleanupError) {
          console.error("Error during cleanup callback:", cleanupError);
        }
      }

      return false;
    }
    // Re-throw non-quota errors
    throw error;
  }
}

// Track if quota warning has been shown this session
let quotaWarningShown = false;

/**
 * Check if a key is related to datafile storage
 * @param {string} key - The storage key
 * @returns {boolean} - True if it's a datafile-related key
 */
function isDatafileKey(key) {
  const datafileKeys = [
    "uploadedFiles",
    "fileLoadData",
    "modelData",
    "graphs",
    "chi2Terms",
    "selectedChi2Terms",
  ];

  return datafileKeys.includes(key);
}

/**
 * Check current session storage usage and warn if approaching limits
 * @param {function} setWarnings - Function to set warnings (optional)
 * @param {function} setNewWarningCount - Function to set warning count (optional)
 */
function checkQuotaUsage(setWarnings = null, setNewWarningCount = null) {
  try {
    // Skip quota checks if monitoring is disabled
    if (quotaMonitoringDisabled) {
      return;
    }

    // Get current session storage usage
    let currentUsage = 0;
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      const value = window.sessionStorage.getItem(key);
      currentUsage += new Blob([key + value]).size;
    }

    // Estimate the quota limit based on browser
    const estimatedQuota = getEstimatedSessionStorageLimit();
    const usagePercentage = (currentUsage / estimatedQuota) * 100;
    // Warn if usage is above 90% (only once per session)
    if (usagePercentage > 90) {
      if (!quotaWarningShown) {
        const warningMessage = `You are running out of memory storage. The app might become sluggish or unstable. Consider removing some datafiles.`;
        console.warn(warningMessage);

        // Use the warning system if available (shows in top bar and toast)
        if (setWarnings && setNewWarningCount) {
          generateWarningObject(
            warningMessage,
            1, // Warning level
            setWarnings,
            setNewWarningCount,
            false, // Not reportable
            "quota-monitoring"
          );
        }

        // Dispatch custom event for quota warning that Dashboard can listen to for toast notifications
        const quotaWarningEvent = new CustomEvent("quotaWarning", {
          detail: {
            message: warningMessage,
            usagePercentage: usagePercentage,
          },
        });
        window.dispatchEvent(quotaWarningEvent);

        // Mark warning as shown
        quotaWarningShown = true;
      }
    } else {
      quotaWarningShown = false;
    }
  } catch (error) {
    console.warn("Could not check quota usage:", error);
  }
}

/**
 * Get estimated session storage limit based on browser
 * @returns {number} - Estimated limit in bytes
 */
function getEstimatedSessionStorageLimit() {
  // All major browsers have a 5MB sessionStorage limit
  return 5 * 1024 * 1024; // 5MB
}

// Track if quota monitoring is disabled during heavy operations
let quotaMonitoringDisabled = false;

/**
 * Disable quota monitoring during heavy operations like session loading
 */
export function disableQuotaMonitoring() {
  quotaMonitoringDisabled = true;
}

/**
 * Re-enable quota monitoring after heavy operations complete
 * @param {function} setWarnings - Function to set warnings (optional)
 * @param {function} setNewWarningCount - Function to set warning count (optional)
 */
export function enableQuotaMonitoring(
  setWarnings = null,
  setNewWarningCount = null
) {
  quotaMonitoringDisabled = false;
  quotaWarningShown = false;
  // Re-check quota immediately after re-enabling, with warning functions if provided
  checkQuotaUsage(setWarnings, setNewWarningCount);
}
export function convertBEParameterToFE(param, BE_Param, forFit = false) {
  let newParam = {
    ...param,
    value:
      param.type !== "Checkbox" ? BE_Param.value : checkBool(BE_Param.value),
  };
  if (!forFit) {
    if (hasProperty(BE_Param, "matched")) {
      newParam.group = BE_Param.matched;
    } else if (Object.prototype.hasOwnProperty.call(newParam, "group")) {
      delete newParam.group;
    }
    if (hasProperty(BE_Param, "hardmax")) {
      newParam.hardMax = BE_Param.hardmax;
    } else if (Object.prototype.hasOwnProperty.call(newParam, "hardMax")) {
      delete newParam.hardMax;
    }
    if (hasProperty(BE_Param, "hardmin")) {
      newParam.hardMin = BE_Param.hardmin;
    } else if (Object.prototype.hasOwnProperty.call(newParam, "hardMin")) {
      delete newParam.hardMin;
    }
    if (BE_Param.fixed && !newParam.fixed) {
      newParam.customFixed = true;
    } else {
      newParam.customFixed = newParam.fixed;
    }
  }
  return newParam;
}

export function convertRangeFromUnitsToUnits(oldUnit, newUnit, range) {
  // If the units are the same, return the range
  let maxX = Math.max(Math.abs(range[0]), Math.abs(range[1]));
  if (oldUnit === newUnit) {
    return range;
  }
  let isInverseOldUnit = isInverseUnit(oldUnit);
  let isInverseNewUnit = isInverseUnit(newUnit);
  // If the units are not inverse, return the convertedrange
  if (!isInverseOldUnit && !isInverseNewUnit) {
    return [
      cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, range[0])),
      cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, range[1])),
    ];
  }
  // If one of the units is inverse, we need to handle the 0 case
  if (range[0] === 0) {
    range[0] = maxX / 1000;
  }
  if (range[1] === 0) {
    range[1] = -maxX / 1000;
  }
  // If both units are inverse or the ranges doesn't cross 0, we can convert back and forth.
  if ((isInverseOldUnit && isInverseNewUnit) || range[1] < 0 || range[0] > 0) {
    let minvalue = cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, range[0]));
    let maxvalue = cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, range[1]));
    if (minvalue < maxvalue) {
      return [minvalue, maxvalue];
    } else {
      return [maxvalue, minvalue];
    }
  }
  // If one the units is inverse and the ranges cross 0, we need to handle the axes flip.
  return [
    cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, -maxX / 1000)),
    cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, maxX / 1000)),
  ];
}

export function convertRangeRequestFromUnitsToCm1(
  oldUnit,
  range,
  nPoints = 1000,
  xUnitOut = units[0]
) {
  let isInverseOldUnit = isInverseUnit(oldUnit);
  let isInverseXUnitOut = isInverseUnit(xUnitOut);
  // If the units are not inverse, return the convertedrange
  if (!isInverseOldUnit && !isInverseXUnitOut) {
    return [
      {
        min: cm1ToAnyUnit(xUnitOut, anyUnitToCm1(oldUnit, range[0])),
        max: cm1ToAnyUnit(xUnitOut, anyUnitToCm1(oldUnit, range[1])),
        nPoints: nPoints,
      },
    ];
  }
  // If one of the units is inverse, we need to handle the 0 case
  if (range[0] === 0) {
    range[0] = range[1] / (nPoints * 100);
  }
  if (range[1] === 0) {
    range[1] = -range[0] / (nPoints * 100);
  }
  // If both units are inverse or the ranges doesn't cross 0, we can convert back and forth.
  if ((isInverseXUnitOut && isInverseOldUnit) || range[1] < 0 || range[0] > 0) {
    let minvalue = cm1ToAnyUnit(xUnitOut, anyUnitToCm1(oldUnit, range[0]));
    let maxvalue = cm1ToAnyUnit(xUnitOut, anyUnitToCm1(oldUnit, range[1]));
    if (minvalue < maxvalue) {
      return [
        {
          min: minvalue,
          max: maxvalue,
          nPoints: nPoints,
        },
      ];
    } else {
      return [
        {
          min: maxvalue,
          max: minvalue,
          nPoints: nPoints,
        },
      ];
    }
  }
  // If one the units is inverse and the ranges cross 0, we need to handle the axes flip.
  const stepSize = (range[1] - range[0]) / (nPoints - 1);
  let nPointsNeg = Math.ceil(-range[0] / stepSize);
  const nPointsPos = Math.ceil(range[1] / stepSize);
  let UpperLimitNeg = range[0] + (nPointsNeg - 1) * stepSize;
  let LowerLimitPos = range[1] - (nPointsPos - 1) * stepSize;
  if (UpperLimitNeg === 0) {
    nPointsNeg--;
    UpperLimitNeg = range[0] + (nPointsNeg - 1) * stepSize;
  }
  if (nPointsNeg < 2) {
    return convertRangeRequestFromUnitsToCm1(
      oldUnit,
      [stepSize / 100, range[1]],
      nPoints,
      xUnitOut
    );
  }
  if (nPointsPos < 2) {
    return convertRangeRequestFromUnitsToCm1(
      oldUnit,
      [range[0], -stepSize / 100],
      nPoints,
      xUnitOut
    );
  }
  UpperLimitNeg = Math.min(UpperLimitNeg, -stepSize / 100);
  LowerLimitPos = Math.max(LowerLimitPos, stepSize / 100);

  return [
    convertRangeRequestFromUnitsToCm1(
      oldUnit,
      [range[0], UpperLimitNeg],
      nPointsNeg,
      xUnitOut
    )[0],
    convertRangeRequestFromUnitsToCm1(
      oldUnit,
      [LowerLimitPos, range[1]],
      nPointsPos,
      xUnitOut
    )[0],
  ];
}

export function convertDataXYFromUnitsToUnits(
  oldUnit,
  newUnit,
  x_input,
  y_input,
  sort = false
) {
  let x = deepCopy(x_input);
  let y = deepCopy(y_input);

  for (let i = 0; i < x.length; i++) {
    x[i] = cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, x[i]));
  }

  if (!sort) {
    return [x, y];
  }
  // Create an array of indices and sort them based on x values
  const indices = Array.from({ length: x.length }, (_, i) => i);
  indices.sort((a, b) => x[a] - x[b]);

  // Reorder both x and y arrays based on sorted indices
  const sortedX = indices.map((i) => x[i]);
  const sortedY = indices.map((i) => y[i]);

  return [sortedX, sortedY];
}

export function convertDataFromUnitsToUnits(
  oldUnit,
  newUnit,
  data_input,
  sort = false
) {
  let data = deepCopy(data_input);
  for (let i = 0; i < data.length; i++) {
    data[i].x = cm1ToAnyUnit(newUnit, anyUnitToCm1(oldUnit, data[i].x));
  }
  // Sort data array by x values in ascending order
  if (sort) {
    data.sort((a, b) => a.x - b.x);
  }
  return data;
}

export function getParsedModelListFromJson(filesData) {
  // const mainModel = JSON.parse(filesData[0].content);

  // We select the first file, since you can only open 1 file in model parameters:
  const listOfModelsToParse = filesData[0].content.split(
    "###MODEL_SEPARATOR###"
  );
  const parsedModels = [];

  for (let i = 0; i < listOfModelsToParse.length; i++) {
    const modelToParse = listOfModelsToParse[i];
    parsedModels.push(JSON.parse(modelToParse));
  }
  for (let i = 0; i < parsedModels.length; i++) {
    if (hasProperty(parsedModels[i], "vdf")) {
      // Keep the VDF data but remove the grid data
      if (
        Object.prototype.hasOwnProperty.call(parsedModels[i].vdf, "gridData")
      ) {
        delete parsedModels[i].vdf.gridData;
      }
    }
  }
  return parsedModels;
}

export function updateNestedModel(models, updatedModel, FE_ID) {
  return models.map((model) => {
    if (model.FE_ID === FE_ID) {
      return updatedModel;
    }

    if (model.subModels && model.subModels.length > 0) {
      return {
        ...model,
        subModels: updateNestedModel(model.subModels, updatedModel, FE_ID),
      };
    }

    return model;
  });
}

export function createDataPayloadForModels(fileIds, files, unitName = "mm") {
  let dataPayload = [];
  // Find unit by value (which is always a string) rather than name (which can be JSX)
  let xUnit = units.find((unit) => unit.value === unitName);
  // Fallback to "mm" if unit not found
  if (!xUnit) {
    xUnit = units.find((unit) => unit.value === "mm");
  }
  // If still not found, use the first unit as fallback
  if (!xUnit && units.length > 0) {
    xUnit = units[0];
  }
  for (let i = 0; i < fileIds.length; i++) {
    const fileId = fileIds[i];
    const file = files.find((file) => file.ID == fileId);
    if (!file) {
      continue;
    }
    let data = file.dataPoints;
    data = filterCurveDataToRange(data, file.dataRangeMin, file.dataRangeMax);

    data = segmentsIntoSingleCurve(
      splitCurveIntoSegmentsByEdges(data, file.edges)
    );
    // Only convert if both units are valid
    if (xUnit) {
      data = convertDataFromUnitsToUnits(units[0], xUnit, data);
    }
    data = data.map((point) => ({
      x: point.x,
      y: point.y,
    }));
    dataPayload.push({ dataid: fileId, data: data });
  }
  return dataPayload;
}
